import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, UserCheck, MessageSquare, Clock, CheckCircle } from "lucide-react";
import { api } from "@/api";
import { errMsg } from "@/utils";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminGuard from "@/components/admin/AdminGuard";
import StatusBadge from "@/components/admin/StatusBadge";
import { Button } from "@/components/ui/button";

const STATUSES = ["pending", "assigned", "scheduled", "completed", "cancelled", "rejected"];

export default function AdminConsultationDetail() {
  const { consultationId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAdvisor, setSelectedAdvisor] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [noteText, setNoteText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [detailRes, advisorRes] = await Promise.all([
        api.get(`/consultations/${consultationId}`),
        api.get("/advisors", { params: { page_size: 100 } }),
      ]);
      setDetail(detailRes.data);
      setAdvisors(advisorRes.data.advisors || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [consultationId]);

  useEffect(() => { load(); }, [load]);

  const assignAdvisor = async () => {
    if (!selectedAdvisor) return;
    setSubmitting(true);
    try {
      await api.post(`/consultations/${consultationId}/assign`, { advisor_id: selectedAdvisor });
      toast.success("Advisor assigned");
      setSelectedAdvisor("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSubmitting(false); }
  };

  const updateStatus = async () => {
    if (!newStatus) return;
    setSubmitting(true);
    try {
      await api.post(`/consultations/${consultationId}/status`, { status: newStatus, note: statusNote || undefined });
      toast.success("Status updated");
      setNewStatus("");
      setStatusNote("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSubmitting(false); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/consultations/${consultationId}/notes`, { note: noteText.trim() });
      toast.success("Note added");
      setNoteText("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <AdminGuard>
      <AdminLayout>
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[hsl(var(--zt-teal))]" size={28} /></div>
      </AdminLayout>
    </AdminGuard>
  );

  if (!detail) return (
    <AdminGuard>
      <AdminLayout>
        <button onClick={() => navigate("/admin/consultations")} className="flex items-center gap-2 text-sm text-[hsl(var(--zt-charcoal))]/60 hover:text-[hsl(var(--zt-teal))] mb-6 font-heading uppercase tracking-wider transition-colors">
          <ArrowLeft size={15} /> Back
        </button>
        <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-16 text-center shadow-sm">
          <p className="text-sm text-[hsl(var(--zt-charcoal))]/40">Consultation not found</p>
        </div>
      </AdminLayout>
    </AdminGuard>
  );

  const c = detail.consultation;
  const isTerminal = ["completed", "cancelled", "rejected"].includes(c?.status);

  return (
    <AdminGuard>
      <AdminLayout>
        <button onClick={() => navigate("/admin/consultations")} className="flex items-center gap-2 text-sm text-[hsl(var(--zt-charcoal))]/60 hover:text-[hsl(var(--zt-teal))] mb-6 font-heading uppercase tracking-wider transition-colors">
          <ArrowLeft size={15} /> Back
        </button>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main detail */}
          <div className="lg:col-span-2 space-y-6">
            {isTerminal && (
              <div className="bg-[hsl(var(--zt-beige))]/60 border border-[hsl(var(--zt-beige-deep))] rounded-2xl px-5 py-3.5 flex items-center gap-3">
                <CheckCircle size={15} className="text-[hsl(var(--zt-charcoal))]/40 flex-shrink-0" />
                <p className="text-sm text-[hsl(var(--zt-charcoal))]/60 font-heading uppercase tracking-wider text-[10px] font-semibold">
                  This consultation is {c?.status} — status and advisor assignment are locked.
                </p>
              </div>
            )}
            {/* Header card */}
            <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-6 shadow-sm">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h2 className="font-display text-xl text-[hsl(var(--zt-teal))] font-bold">{c?.subject}</h2>
                  <p className="text-sm text-[hsl(var(--zt-charcoal))]/55 mt-1">{c?.user_name} · {c?.user_email}</p>
                </div>
                <StatusBadge status={c?.status} />
              </div>
              {c?.description && (
                <p className="text-sm text-[hsl(var(--zt-charcoal))]/70 bg-[hsl(var(--zt-beige))]/50 rounded-xl p-4 border border-[hsl(var(--zt-beige-deep))]/50">
                  {c.description}
                </p>
              )}
              <div className="mt-4 pt-4 border-t border-[hsl(var(--zt-beige-deep))] grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[10px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/40 mb-1">Advisor</p>
                  <p className="font-semibold text-[hsl(var(--zt-teal))]">{c?.advisor_name || <span className="text-[hsl(var(--zt-charcoal))]/35 italic font-normal">Unassigned</span>}</p>
                </div>
                <div>
                  <p className="text-[10px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/40 mb-1">Created</p>
                  <p className="font-semibold text-[hsl(var(--zt-teal))]">{fmtDate(c?.created_at)}</p>
                </div>
              </div>
            </div>

            {/* Internal notes */}
            {c?.internal_notes && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
                <h3 className="font-heading uppercase tracking-[0.18em] text-[10px] text-amber-700 font-bold mb-3">Internal Notes</h3>
                <pre className="text-sm text-amber-800 whitespace-pre-wrap font-body">{c.internal_notes}</pre>
              </div>
            )}

            {/* Timeline */}
            <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-[hsl(var(--zt-beige-deep))] bg-[hsl(var(--zt-beige))]/40">
                <h3 className="font-heading uppercase tracking-[0.18em] text-[11px] text-[hsl(var(--zt-teal))]/70 font-bold">Timeline</h3>
              </div>
              {detail?.timeline?.length === 0 ? (
                <p className="px-6 py-8 text-sm text-center text-[hsl(var(--zt-charcoal))]/40">No timeline entries</p>
              ) : (
                <ul className="px-6 py-4 space-y-4">
                  {detail?.timeline?.map(t => (
                    <li key={t.id} className="flex gap-3">
                      <div className="w-7 h-7 rounded-full bg-[hsl(var(--zt-teal))]/8 border border-[hsl(var(--zt-teal))]/15 grid place-items-center flex-shrink-0 mt-0.5">
                        <Clock size={12} className="text-[hsl(var(--zt-teal))]" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[hsl(var(--zt-teal))]">{t.action}</p>
                        {t.note && <p className="text-[11px] text-[hsl(var(--zt-charcoal))]/60 mt-0.5">{t.note}</p>}
                        <p className="text-[10px] text-[hsl(var(--zt-charcoal))]/40 mt-0.5">{t.actor_name} · {fmtDateTime(t.created_at)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Actions sidebar */}
          <div className="space-y-4">
            {/* Assign advisor */}
            {!isTerminal && (
            <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-5 shadow-sm">
              <h3 className="font-heading uppercase tracking-[0.18em] text-[11px] text-[hsl(var(--zt-teal))]/70 font-bold mb-4">
                <UserCheck size={13} className="inline mr-2" />Assign Advisor
              </h3>
              <select
                value={selectedAdvisor}
                onChange={e => setSelectedAdvisor(e.target.value)}
                className="w-full h-9 px-3 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30 mb-3"
              >
                <option value="">Select advisor…</option>
                {advisors.map(a => <option key={a.advisor_id} value={a.advisor_id}>{a.name}</option>)}
              </select>
              <Button onClick={assignAdvisor} disabled={!selectedAdvisor || submitting} size="sm"
                className="w-full bg-[hsl(var(--zt-teal))] text-white rounded-xl h-9 font-heading uppercase tracking-wider text-xs">
                Assign
              </Button>
            </div>
            )}

            {/* Update status */}
            {!isTerminal && (
            <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-5 shadow-sm">
              <h3 className="font-heading uppercase tracking-[0.18em] text-[11px] text-[hsl(var(--zt-teal))]/70 font-bold mb-4">
                <CheckCircle size={13} className="inline mr-2" />Update Status
              </h3>
              <select
                value={newStatus}
                onChange={e => setNewStatus(e.target.value)}
                className="w-full h-9 px-3 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30 mb-3"
              >
                <option value="">Select status…</option>
                {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
              <textarea
                value={statusNote}
                onChange={e => setStatusNote(e.target.value)}
                placeholder="Optional note…"
                className="w-full h-20 px-3 py-2 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30 resize-none mb-3"
              />
              <Button onClick={updateStatus} disabled={!newStatus || submitting} size="sm"
                className="w-full bg-[hsl(var(--zt-gold))] text-white rounded-xl h-9 font-heading uppercase tracking-wider text-xs">
                Update Status
              </Button>
            </div>
            )}

            {/* Add note — always visible */}
            <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-5 shadow-sm">
              <h3 className="font-heading uppercase tracking-[0.18em] text-[11px] text-[hsl(var(--zt-teal))]/70 font-bold mb-4">
                <MessageSquare size={13} className="inline mr-2" />Internal Note
              </h3>
              <textarea
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                placeholder="Add internal note…"
                className="w-full h-24 px-3 py-2 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30 resize-none mb-3"
              />
              <Button onClick={addNote} disabled={!noteText.trim() || submitting} size="sm"
                className="w-full bg-[hsl(var(--zt-teal))]/10 text-[hsl(var(--zt-teal))] border border-[hsl(var(--zt-teal))]/20 rounded-xl h-9 font-heading uppercase tracking-wider text-xs hover:bg-[hsl(var(--zt-teal))]/20">
                Add Note
              </Button>
            </div>
          </div>
        </div>
      </AdminLayout>
    </AdminGuard>
  );
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
function fmtDateTime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
